'use client';

import type { Dispatch, SetStateAction } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';

import type { RadioGroupItemProps } from '@capsule/consumer-design-system';
import { RadioButtonGroup, SubmitButton, Typography } from '@capsule/consumer-design-system';

import { useMediaQuery } from '@react-hookz/web';
import { Form, Formik } from 'formik';
import debounce from 'lodash/debounce';
import { useTranslations } from 'next-intl';
import { css, useTheme } from 'styled-components';
import * as Yup from 'yup';

import capsuleLogo from 'public/capsulerx-logo.svg';
import CapsuleIcon from 'src/app/components/Elements/CapsuleIcon';
import MarketingPageSection from 'src/app/components/Elements/MarketingPageSection';
import { Skeleton } from 'src/app/components/Elements/Skeleton';
import { usePharmacySearch } from 'src/app/hooks/data/pharmacy';
import type { PlaceDetail } from 'src/app/types';
import { CAPSULE_ICON_STRINGS, FORM_INPUT_ICON_STRINGS } from 'src/consts';

import { ClearRxLayout } from '../../ClearRxLayout';
import { FauxLabel, FormikTextFieldWithTextareaStyles, TextFieldTransparentButton } from '../styles';

import { SearchResultsView } from './SearchResultsView';
import { CapsuleLogo, MedInStockTag, PharmacySearchContainer } from './styles';

import type { SelectedPharmacy } from '.';
import { PharmacyHubStep } from '.';

type ConfirmPharmacyProps = {
  setSelectedPharmacy: Dispatch<SetStateAction<SelectedPharmacy>>;
  setStep: (step: PharmacyHubStep) => void;
};

export const ConfirmPharmacy = ({ setSelectedPharmacy, setStep }: ConfirmPharmacyProps) => {
  const theme = useTheme();
  const t = useTranslations('pharmacyHubJourney.confirmPharmacy');

  /* Functionality and state for the text area element */
  const textInputRef = useRef<HTMLInputElement | null>(null);
  const textFieldBackButtonRef = useRef<HTMLButtonElement | null>(null);
  const textFieldClearButtonRef = useRef<HTMLButtonElement | null>(null);

  const [isFullScreen, setFullScreen] = useState(false);
  const [isLoadingOptions, setIsLoadingOptions] = useState(false);
  const [pharmacySearchInput, setPharmacySearchInput] = useState<string>('');
  const [shouldShowResults, setShouldShowResults] = useState<boolean>(false);
  const [searchResults, setSearchResults] = useState<PlaceDetail[]>([]);

  const isMobile = useMediaQuery(theme.breakpoints.smallDown) ?? false;
  const searchBoxLeftIcon =
    !isFullScreen && typeof document !== undefined && !(textInputRef.current === document.activeElement)
      ? FORM_INPUT_ICON_STRINGS['system_search']
      : FORM_INPUT_ICON_STRINGS['chevron_left'];

  const { data: pharmacyResults, refetch, isFetching } = usePharmacySearch(pharmacySearchInput || '', '', 5000);

  const [options, setOptions] = useState<RadioGroupItemProps[]>([
    {
      label: <CapsuleLogo src={capsuleLogo} />,
      value: 'Capsule',
      description: (
        <>
          {t('freeDeliveryText', { zipCode: '10022' })}
          <Typography variant="small">{t('deliveryUntilText', { time: '10pm' })}</Typography>
        </>
      ),
      subDescription: <MedInStockTag>{t('medsInStockText', { count: '2', total: '2' })}</MedInStockTag>,
      descriptionColor: 'capsuleGreen60',
    },
    {
      label: 'Walgreens',
      value: 'Walgreens',
      description: '1284 5th ave, New York',
      subDescription: t('closesAtText', { time: '7pm' }),
    },
  ]);

  const validationSchema = Yup.object({
    pharmacy: Yup.string().required(t('selectError')).default(''),
  });
  const initialValues = validationSchema.cast({});

  useEffect(() => {
    if (isMobile && shouldShowResults && isFullScreen) {
      setTimeout(() => {
        window.scroll({ top: 0 });
      }, 10);
    } else if (shouldShowResults) {
      textInputRef?.current?.scrollIntoView({
        block: 'start',
        behavior: 'smooth',
        inline: 'nearest',
      });
    }
  }, [shouldShowResults]);

  useEffect(() => {
    if (isFullScreen && isMobile) {
      document.body.style.overflowY = 'hidden';
    } else {
      document.body.style.overflowY = '';
    }
  }, [isFullScreen, isMobile]);

  const debouncedRefetch = useCallback(
    debounce(() => refetch(), 400),
    [refetch]
  );

  useEffect(() => {
    if (pharmacySearchInput.length > 0) {
      debouncedRefetch();
      setShouldShowResults(true);
    } else {
      setSearchResults([]);
    }
    if (isMobile && pharmacySearchInput.length > 0) {
      setFullScreen(true);
    }
  }, [pharmacySearchInput]);

  useEffect(() => {
    if (pharmacySearchInput.length > 0) {
      setSearchResults(pharmacyResults?.data.results || []);
    }
  }, [pharmacyResults?.data.results]);

  const handlePharmacySearchOnChange = () => {
    setPharmacySearchInput(textInputRef?.current?.value || '');
  };

  const handleOnFocus = () => {
    if (isMobile) {
      if (pharmacySearchInput.length > 0) {
        setFullScreen(true);
        if (searchResults.length > 0) setShouldShowResults(true);
      }
    } else {
      if (pharmacySearchInput.length > 0) setShouldShowResults(true);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const fetchPharmacy = async () => {
      await debouncedRefetch();
    };
    if (textInputRef.current === null) return;
    switch (e.key) {
      case 'Enter':
        fetchPharmacy();
        textInputRef.current.blur();
        break;
      case 'Escape':
        textInputRef.current.blur();
        break;
    }
  };

  return (
    <MarketingPageSection>
      <ClearRxLayout>
        <Typography
          variant="heading1"
          css={css`
            margin-bottom: ${({ theme }) => theme.space.s4};
          `}
        >
          {t('title')}
        </Typography>
        <Typography
          variant="body"
          css={css`
            margin-bottom: ${({ theme }) => theme.space.s6};
          `}
        >
          {t('subtitle')}
        </Typography>
        <Formik
          initialValues={initialValues}
          validateOnBlur={false}
          validationSchema={validationSchema}
          onSubmit={async (values, { setErrors }) => {
            const selected = options.find(i => i.value === values.pharmacy);
            if (!selected) {
              setErrors({ pharmacy: t('submitError') });
              return;
            }
            const selectedPharmacy = {
              name: selected.value === 'Capsule' ? 'Capsule' : `${selected.label}`,
              address: selected.value === 'Capsule' ? undefined : selected.description,
              phone: selected.value === 'Capsule' ? undefined : '212-555-1212',
            };
            await setSelectedPharmacy(selectedPharmacy);
            setStep(PharmacyHubStep.success);
          }}
        >
          {formikBag => {
            const handleSelectPharmacySearchItem = async (item: PlaceDetail) => {
              // This ensures we always re render even if options arent updated
              // for the updated selected item
              await setIsLoadingOptions(true);

              setPharmacySearchInput('');
              formikBag.setFieldValue('searchPharmName', '');
              const isItemAlreadySelected = options.find(opt => opt.value == item.place_id);
              if (!isItemAlreadySelected) {
                await setOptions(
                  options.concat([
                    {
                      label: item.name,
                      value: item.place_id,
                      description: item.formatted_address,
                      subDescription: t('closesAtText', { time: '7pm' }),
                    },
                  ])
                );
              }
              formikBag.setFieldValue('pharmacy', item.place_id);
              setIsLoadingOptions(false);
              setShouldShowResults(false);
              setFullScreen(false);
            };
            return (
              <Form>
                {isLoadingOptions ? (
                  <Skeleton />
                ) : (
                  <RadioButtonGroup
                    css={css`
                      margin-bottom: ${({ theme }) => theme.space.s5};
                    `}
                    onChange={formikBag.handleChange}
                    label={t('selectLabel')}
                    error={formikBag.errors.pharmacy}
                    isValid={!formikBag.errors.pharmacy || !formikBag.touched.pharmacy}
                    options={options}
                    name={'pharmacy'}
                    defaultValue={formikBag.values.pharmacy}
                  />
                )}
                <PharmacySearchContainer isFullScreen={isFullScreen} isMobile={isMobile}>
                  <FauxLabel
                    variant="overline"
                    hasError={false}
                    isFocused={textInputRef.current === document.activeElement}
                  >
                    {t('findAnotherPharmacy')}
                  </FauxLabel>
                  <FormikTextFieldWithTextareaStyles
                    name="searchPharmName"
                    onBlur={e => {
                      // Duplicates logic in the actual buttons because the TextInput intercepts clicks on blur.
                      if (e.relatedTarget === textFieldBackButtonRef.current) {
                        if (searchResults.length == 0) {
                          setShouldShowResults(false);
                        }
                        if (isMobile) {
                          setFullScreen(false);
                          setShouldShowResults(false);
                        }
                      } else if (e.relatedTarget === textFieldClearButtonRef.current && !!textInputRef.current) {
                        formikBag.setFieldValue('searchPharmName', '');
                      }
                    }}
                    ref={textInputRef}
                    label={{
                      content: t('searchPlaceholder'),
                      isHidden: true,
                    }}
                    adornment={
                      isMobile
                        ? {
                            left: (
                              <>
                                <TextFieldTransparentButton
                                  type="button"
                                  onClick={() => {
                                    if (isFullScreen) {
                                      formikBag.setFieldValue('searchPharmName', '');
                                      setPharmacySearchInput('');
                                      setShouldShowResults(false);
                                      setFullScreen(false);
                                    }
                                  }}
                                  ref={textFieldBackButtonRef}
                                />
                                <CapsuleIcon
                                  iconString={searchBoxLeftIcon}
                                  css={css`
                                    padding: 0px !important;
                                    height: 36px !important;
                                    width: 36px !important;
                                    svg {
                                      height: 36px !important;
                                      width: 36px !important;
                                    }
                                    margin-bottom: ${({ theme }) => theme.space.s3};
                                  `}
                                />
                              </>
                            ),
                            right: (
                              <>
                                <TextFieldTransparentButton
                                  type="button"
                                  onClick={() => {
                                    formikBag.setFieldValue('searchPharmName', '');
                                    setPharmacySearchInput('');
                                    textInputRef?.current?.focus();
                                  }}
                                  ref={textFieldClearButtonRef}
                                />
                                <CapsuleIcon
                                  iconString={CAPSULE_ICON_STRINGS.close}
                                  css={css`
                                    padding: 0px !important;
                                    height: 36px !important;
                                    width: 36px !important;
                                    svg {
                                      height: 36px !important;
                                      width: 36px !important;
                                    }
                                    margin-bottom: ${({ theme }) => theme.space.s3};
                                  `}
                                />
                              </>
                            ),
                          }
                        : undefined
                    }
                    placeholder={t('searchPlaceholder')}
                    onFocus={handleOnFocus}
                    onKeyDown={handleKeyDown}
                    onChange={handlePharmacySearchOnChange}
                    autoComplete="off"
                    type="text"
                  />
                  <SearchResultsView
                    isFetchingNearby={isFetching}
                    nearbyPlaces={searchResults}
                    selectItem={handleSelectPharmacySearchItem}
                    showSearchResults={shouldShowResults}
                    displayProps={{
                      visualization: null,
                      setVisualization: null,
                      isFullScreen: isFullScreen,
                    }}
                  />
                </PharmacySearchContainer>
                <SubmitButton
                  isSubmitting={false}
                  isFullWidth
                  variant="contained-secondary-lg"
                  css={css`
                    margin-top: ${({ theme }) => theme.space.s7};
                  `}
                >
                  {t('confirmButton')}
                </SubmitButton>
              </Form>
            );
          }}
        </Formik>
      </ClearRxLayout>
    </MarketingPageSection>
  );
};
